import React, { CSSProperties } from 'react';
import { useEffect, useState, useRef } from 'react';
import { useAppSelector, useAppDispatch } from '../Core/hooks';
import { getUser } from "../Store/Auth";
import { getProject } from '../Store/Project';
import { Roadmap } from '../Models/Roadmap';
import { getSettings } from '../Store/Settings';
import ChatEditor from '../Components/ChatEditor';
import { getChat, setMessages } from '../Store/Chat';
import { Message } from '../Models/Message';
import Avatar from './Avatar';
import { format, isSameDay, isToday, isYesterday } from 'date-fns';
import PopupContextMenu from '../Modals/PopupContext';
import { PopupContextParam } from '../Models/Requests/PopupContextParam';
import { openSocket } from '../Helpers/openSocket';

function ChatMain() {

    const chatState = useAppSelector(getChat);
    const userState = useAppSelector(getUser);
    const settingsState = useAppSelector(getSettings);
    const dispatch = useAppDispatch();
    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const [popupParams, setPopupParams] = useState<PopupContextParam>();
      
    // Serialize the `loaded` state across all channels
    const loadedState = JSON.stringify(
        chatState.channels.map((c) => ({ id: c._id, loaded: c.loaded }))
    );

    useEffect(() => {
        const timeout = setTimeout(scrollToBottom, 50);
        return () => clearTimeout(timeout);
    }, []); // On mount
    
    useEffect(() => {
        const timeout = setTimeout(scrollToBottom, 50);
        return () => clearTimeout(timeout);
    }, [loadedState, chatState.messages]); // On message updates

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const handleDeleteMessage = (e: React.MouseEvent<HTMLDivElement>, id: string) => {
        e.preventDefault();
        e.stopPropagation();
        /* Show the delete confirmation window */
        setPopupParams({
            _id: id,
            event: e,
            buttonClass: "btnDelete",
            buttonText: "Delete",
            closeHandler: closeDeleteMessagePopup,
            buttonHandlerWithId: deleteMessageConfirmed,
            message: "Dele this message? There is no undo.",
            headingText: "Delete Message",
            backButton: false,
        });
    }
    
    const closeDeleteMessagePopup = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setPopupParams({
            _id: "",
            event: e,
            buttonClass: "",
            buttonText: "",
            closeHandler: closeDeleteMessagePopup,
            buttonHandlerWithId: deleteMessageConfirmed,
            message: "",
            headingText: "",
            backButton: false,
        });
    }
    
    const deleteMessageConfirmed = (e: React.MouseEvent<HTMLDivElement>, id: string) => {
        e.preventDefault();
        e.stopPropagation();

        let messages = [...chatState.messages];
        let index = messages.findIndex(m => m._id === id);
        if (index !== -1) {
            /* Remove from frontend */
            messages.splice(index, 1);
            dispatch(setMessages(messages));
            /* Remove from backend */
            //dispatch(deleteMessage(id));
            let socket = openSocket(dispatch, settingsState);
            if (socket && channel) {
                socket.emit("deleteMessage", { 
                    id: id,
                });
            }

            closeDeleteMessagePopup(e);
        }
    }

    let messageParentStyle: CSSProperties = {
        position: "relative", 
        height: "89vh",
        //height: (chatState.typing) ? "86vh" : "89vh",
    }

    let messageStyle: CSSProperties = { 
        height: "72vh",
        //height: (chatState.typing) ? "69vh" : "72vh", 
        overflowY: "auto", 
        flexGrow: 1
    };

    let messages: Array<Message> = [];
    let formattedMessages: Array<JSX.Element | undefined> = [];
    let channel = chatState.channels.find(c => c.loaded);
    if (channel) {
        messages = chatState.messages.filter(m => m.channelId === channel!._id);
        messages.sort((a, b) => a.sort - b.sort);
    
        formattedMessages = messages.map((message, index) => {
            let poster = channel!.members.find(m => m.userId === message.userId);
            const current = new Date(message.modified);
            const previous = index > 0 ? new Date(messages[index - 1].modified) : null;
            const dayChanged = !previous || !isSameDay(current, previous);    
            
            let displayDate: string;

            if (isToday(current)) {
                displayDate = "Today";
            } else if (isYesterday(current)) {
                displayDate = "Yesterday";
            } else {
                displayDate = format(current, "EEEE, MMMM do");
            }

            return (
                <div key={index}>
                    {dayChanged &&
                    <>
                        <div style={{ position: "relative", margin: "0" }}>
                            <div style={{
                                borderBottom: "1px solid #d3d2d2",
                                width: "100%",
                                position: "absolute",
                                top: "50%",
                                left: 0,
                                zIndex: 0
                            }}></div>

                            <div style={{
                                position: "relative",
                                zIndex: 1,
                                margin: "0 auto",
                                background: "white",
                                padding: "5px 20px",
                                border: "1px solid #d3d2d2",
                                borderRadius: "30px",
                                width: "fit-content",
                                //width: "125px",
                                textAlign: "center",
                                fontSize: ".85em",
                                fontWeight: "bold"
                            }}>
                                {displayDate}
                            </div>
                        </div>
                    </>
                    }
                    <div 
                        className="message-row"
                        style={{ 
                            display: "flex",
                            alignItems: "flex-start",
                            minHeight: "1vh",
                            //marginBottom: (index !== messages.length - 1) ? "10px" : "0px",
                            padding: "5px",
                        }}
                        onContextMenu={(userState._id === message.userId) ? (e) => handleDeleteMessage(e, message._id ) : undefined}
                    >
                        <div style={{ display: "inline-block", marginRight: "5px", position: "relative", top: "5px" }}>
                            <Avatar member={poster} size="xsmall" />
                        </div>
                        <div style={{ display: "inline-block", fontSize: ".85em"  }}>
                            <div style={{ fontWeight: "bold" }}>
                                {poster ? poster.firstName + " " + poster.lastName : userState.firstName + " " + userState.lastName}
                                <span style={{ color: "gray", paddingLeft: "5px", fontWeight: "500" }}>
                                    {format(new Date(message.modified), 'h:mm a')}
                                </span>
                            </div>
                            <div 
                                className="message"
                                dangerouslySetInnerHTML={{
                                __html: message.message.replace(/\n/g, "<br>")
                            }} />
                        </div>
                    </div>
                </div>
            )
        });
    }

    return (
        <>
        <div className="rightContentDefault" style={{ marginTop: "-5px" }}>
            {channel ?
                <div style={messageParentStyle}>
                    <div style={messageStyle}>
                        {formattedMessages}
                        <div ref={messagesEndRef} />
                    </div>
                    <div style={{ position: "absolute", bottom: "0", left: "0", width: "100%", backgroundColor: "#fff" }}>
                        {chatState.typing &&
                            <div style={{ zIndex: "9999", position: "relative", backgroundColor: "#fff", paddingBottom: "5px" }}>
                                <img src='images/typing-bubble.gif' style={{ width: "50px" }} /><br />
                                <span style={{ fontSize: "10pt", fontStyle: "italic" }}>
                                    <b>{chatState.typingNames.join(", ")}</b> is typing
                                </span>
                            </div>
                        }
                        <ChatEditor />
                    </div>
                </div>
            :
                <div style={{ margin: "100px auto 0px auto" }}>
                    <div style={{ textAlign: "center" }}>
                        <img src="/images/herdr-orange-logo-cat-v1.png"
                            style={{ width: "150px", opacity: ".9" }}
                        />    
                    </div>
                    <div style={{ textAlign: "center", marginTop: "20px", fontWeight: "500" }}>
                        Add a channel or direct message to get started...
                    </div>
                </div>
            }
        </div>
        <PopupContextMenu params={popupParams} />
        </>
    );
}

export default ChatMain;